import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Label } from '../ui/label';
import { Plus, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select"

const ServiceBillRow = React.memo(({ index, bill, onUpdate, onRemove, billType, errors = {} }) => {
    // Local state for fast inputs
    const [localDescription, setLocalDescription] = useState(bill.description || '');
    const [localCost, setLocalCost] = useState(bill.serviceCost || 0);
    const [localQuantity, setLocalQuantity] = useState(bill.quantity ?? 0);

    // Sync from props if changed externally
    useEffect(() => {
        setLocalDescription(bill.description || '');
    }, [bill.description]);

    useEffect(() => {
        setLocalCost(bill.serviceCost || 0);
    }, [bill.serviceCost]);

    useEffect(() => {
        setLocalQuantity(bill.quantity ?? 0);
    }, [bill.quantity]);

    // Handlers
    const handleDescriptionChange = (e) => {
        setLocalDescription(e.target.value);
    };

    const handleCostChange = (e) => {
        setLocalCost(e.target.value);
    };

    const handleQuantityChange = (e) => {
        setLocalQuantity(e.target.value);
    };

    // Debounce updates
    useEffect(() => {
        const handler = setTimeout(() => {
            if (localDescription !== bill.description) {
                onUpdate(index, 'description', localDescription);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [localDescription, bill.description, index, onUpdate]);

    useEffect(() => {
        const handler = setTimeout(() => {
            const val = parseFloat(localCost) || 0;
            if (val !== bill.serviceCost) {
                onUpdate(index, 'serviceCost', val);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [localCost, bill.serviceCost, index, onUpdate]);

    useEffect(() => {
        const handler = setTimeout(() => {
            const val = parseInt(localQuantity, 10);
            const cleanVal = isNaN(val) ? 0 : val;
            if (cleanVal !== (bill.quantity ?? 0)) {
                onUpdate(index, 'quantity', cleanVal);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [localQuantity, bill.quantity, index, onUpdate]);


    const cost = parseFloat(localCost) || 0;
    const qty = parseInt(localQuantity, 10) || 0;
    const gstPercent = parseFloat(bill.gstPercent) || 0; // Default 0%
    const subtotal = cost * qty;
    const taxAmount = (subtotal * gstPercent) / 100;
    const totalAmount = billType === 'Invoice' ? subtotal + taxAmount : subtotal;

    return (
        <Card className="relative overflow-hidden group border-muted-foreground/20 hover:border-blue-400/40 transition-all duration-300 shadow-sm bg-white dark:bg-slate-900 text-slate-900 dark:text-white">
            <CardContent className="p-4 md:p-5">
                <div className="grid grid-cols-2 md:grid-cols-12 gap-4 md:gap-3 items-start">
                    {/* Product / Item Column */}
                    <div className="col-span-2 md:col-span-4 space-y-1">
                        <Label className="md:hidden text-slate-400 dark:text-slate-200 font-semibold">Product / Item <span className="text-destructive">*</span></Label>
                        <Input
                            placeholder="e.g. Laptop Repair"
                            value={localDescription}
                            onChange={handleDescriptionChange}
                            className={cn(
                                "font-medium bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white placeholder-slate-400",
                                errors[`service_${index}_description`] && "border-rose-400 bg-rose-50/50 ring-2 ring-rose-500/10 text-black"
                            )}
                        />
                        {errors[`service_${index}_description`] && <p className="text-[10px] font-black text-rose-600 mt-1 uppercase tracking-tight">{errors[`service_${index}_description`]}</p>}
                    </div>

                    {/* Qty Column */}
                    <div className="col-span-1 md:col-span-2 space-y-1">
                        <Label className="md:hidden text-slate-400 dark:text-slate-200 font-semibold">Quantity</Label>
                        <Input
                            type="number"
                            min="0"
                            value={localQuantity}
                            onChange={handleQuantityChange}
                            onWheel={(e) => e.currentTarget.blur()}
                            className={cn(
                                "w-full md:w-24 min-w-[4.5rem] max-w-[6.5rem] font-bold text-center bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white placeholder-slate-400",
                                errors[`service_${index}_quantity`] && "border-rose-400 bg-rose-50/50 ring-2 ring-rose-500/10 text-black"
                            )}
                        />
                        {errors[`service_${index}_quantity`] && <p className="text-[10px] font-black text-rose-600 mt-1 uppercase tracking-tight">{errors[`service_${index}_quantity`]}</p>}
                    </div>

                    {/* Unit Price Column */}
                    <div className="col-span-1 md:col-span-2 space-y-1">
                        <Label className="md:hidden text-slate-400 dark:text-slate-200 font-semibold">Unit Price</Label>
                        <Input
                            type="number"
                            min="0"
                            value={localCost}
                            onChange={handleCostChange}
                            className={cn(
                                "w-full md:flex-1 md:min-w-[6rem] md:max-w-[12rem] font-bold text-center text-lg bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white placeholder-slate-400",
                                errors[`service_${index}_serviceCost`] && "border-rose-400 bg-rose-50/50 ring-2 ring-rose-500/10 text-black"
                            )}
                        />
                        {errors[`service_${index}_serviceCost`] && <p className="text-[10px] font-black text-rose-600 mt-1 uppercase tracking-tight">{errors[`service_${index}_serviceCost`]}</p>}
                    </div>

                    {/* Total Column */}
                    <div className="col-span-2 md:col-span-3 space-y-1">
                        <Label className="md:hidden text-slate-400 dark:text-slate-200 font-semibold">Total (Inc. GST)</Label>
                        <div className="h-10 w-full flex items-center font-bold text-slate-900 dark:text-white justify-center text-center bg-slate-100 dark:bg-slate-800 rounded-lg px-4 text-sm md:text-base border border-slate-300 dark:border-slate-600 shadow-inner tabular-nums">
                            ₹ {totalAmount.toFixed(2)}
                        </div>
                    </div>

                    {/* Action Column */}
                    <div className="hidden md:flex items-center justify-center col-span-1 self-start mt-1">
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => onRemove(index)}
                            className="text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors rounded-full"
                        >
                            <X className="h-5 w-5" />
                        </Button>
                    </div>

                    {/* Mobile Action */}
                    <div className="md:hidden absolute top-2 right-2">
                        <Button type="button" variant="ghost" size="icon" onClick={() => onRemove(index)} className="h-6 w-6 text-muted-foreground hover:text-red-600 bg-red-50/50 rounded-full shadow-sm">
                            <X className="h-3 w-3" />
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
});

const ServiceBillForm = ({ serviceBills = [], billType, onAdd, onUpdate, onRemove, errors = {} }) => {
    return (
        <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 border border-slate-200/60 dark:border-slate-700/80 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100/50 dark:bg-blue-900/40 rounded-full blur-3xl -mr-10 -mt-10"></div>
                <div className="flex flex-col gap-1 relative z-10">
                    <h4 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-700 to-indigo-700 dark:from-cyan-300 dark:to-blue-300 w-full sm:w-auto tracking-tight">Service / Labor Charges</h4>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-300">Add maintenance, repair, and other services</p>
                </div>
                <Button type="button" onClick={onAdd} variant="default" size="sm" className="w-full sm:w-auto shadow-md hover:shadow-lg transition-all shrink-0 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                    <Plus className="mr-2 h-4 w-4" />
                    Add Service Row
                </Button>
            </div>

            {serviceBills.length > 0 && (
                <div className="hidden md:grid grid-cols-12 gap-3 px-6 pt-2 pb-1 text-[11px] font-bold text-slate-700 dark:text-slate-200 uppercase tracking-widest border-b border-transparent">
                    <div className="col-span-4">Product / Item <span className="text-red-500">*</span></div>
                    <div className="col-span-2">Quantity</div>
                    <div className="col-span-2">Unit Price</div>
                    <div className="col-span-3 text-right">Total (Inc. GST)</div>
                    <div className="col-span-1 text-center">Action</div>
                </div>
            )}

            {serviceBills.length === 0 ? (
                <div className="text-sm font-medium text-muted-foreground dark:text-slate-200 text-center py-10 border border-dashed rounded-2xl bg-slate-50/50 dark:bg-slate-800/70 shadow-inner">
                    No service charges added.
                </div>
            ) : (
                <div className="space-y-3">
                    {serviceBills.map((bill, index) => (
                        <ServiceBillRow
                            key={index}
                            index={index}
                            bill={bill}
                            billType={billType}
                            onUpdate={onUpdate}
                            onRemove={onRemove}
                            errors={errors}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
export default React.memo(ServiceBillForm);
